// src/services/homeContent.ts
import api from "../lib/api";
import type { Industry } from './industry';
import type { Testimonial } from './testimonial';

// ============================================
// TYPE DEFINITIONS
// ============================================

export type ContentType = 'featured_industries' | 'featured_experts' | 'featured_testimonials' | 'marketplace_items';

export interface HomeContent {
  id: string;
  contentType: ContentType;
  resourceId: string;
  displayOrder: number;
  isActive: boolean;
  updatedBy?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  updater?: {
    firstName: string;
    lastName: string;
  };
  resource?: any; // This will be the actual resource data
}

export interface HomeContentGrouped {
  content: {
    featured_industries?: Array<{
      id: string;
      displayOrder: number;
      metadata: Record<string, any>;
      resource: Industry;
    }>;
    featured_experts?: Array<{
      id: string;
      displayOrder: number;
      metadata: Record<string, any>;
      resource: any;
    }>;
    featured_testimonials?: Array<{
      id: string;
      displayOrder: number;
      metadata: Record<string, any>;
      resource: Testimonial;
    }>;
  };
}

export interface HomeContentInput {
  contentType: ContentType;
  resourceId: string;
  displayOrder?: number;
  metadata?: Record<string, any>;
}

export interface HomeContentUpdate {
  displayOrder?: number;
  metadata?: Record<string, any>;
  isActive?: boolean;
}

export interface ReorderItem {
  id: string;
  displayOrder: number;
}

// ============================================
// PUBLIC API ENDPOINTS
// ============================================

/**
 * Get home content for public display
 */
export async function getHomeContent(contentType?: ContentType): Promise<HomeContentGrouped> {
  const params = new URLSearchParams();
  if (contentType) params.append('contentType', contentType);

  const { data } = await api.get(`/api/home-content?${params.toString()}`);
  return data.data;
}

// ============================================
// ADMIN API ENDPOINTS
// ============================================

/**
 * Get all home content (admin view with full details)
 */
export async function getAllHomeContent(contentType?: ContentType): Promise<HomeContent[]> {
  const params = new URLSearchParams();
  if (contentType) params.append('contentType', contentType);

  const { data } = await api.get(`/api/admin/home-content?${params.toString()}`);
  return data.data.homeContents;
}

/**
 * Get available resources for featuring
 */
export async function getAvailableResources(contentType: ContentType): Promise<any[]> {
  const params = new URLSearchParams();
  params.append('contentType', contentType);

  const { data } = await api.get(`/api/admin/home-content/available?${params.toString()}`);
  return data.data.resources;
}

/**
 * Add new home content
 */
export async function addHomeContent(content: HomeContentInput): Promise<HomeContent> {
  const { data } = await api.post('/api/admin/home-content', content);
  return data.data.homeContent;
}

/**
 * Update home content
 */
export async function updateHomeContent(id: string, updates: HomeContentUpdate): Promise<HomeContent> {
  const { data } = await api.patch(`/api/admin/home-content/${id}`, updates);
  return data.data.homeContent;
}

/**
 * Reorder home content items
 */
export async function reorderHomeContent(items: ReorderItem[]): Promise<void> {
  await api.patch('/api/admin/home-content/reorder', { items });
}

/**
 * Delete home content
 */
export async function deleteHomeContent(id: string): Promise<void> {
  await api.delete(`/api/admin/home-content/${id}`);
}
